// The lifetime of one general JuisciGPT turn, as both sides of the wire read it.
//
// Lives in shared/ because the webapp needs the same two numbers the api runs
// on: a conversation carries an in-flight marker (`activeTurnStartedAt` +
// `activeTurnQuestion`) while its turn is being written, and every reader —
// the route that refuses to delete a busy conversation, the list that shows a
// turn still being answered, the client that polls for the answer after its
// connection broke — has to agree on when such a marker is a live turn and
// when it is the debris of an instance that died mid-turn. Restating the
// ceiling on either side is how the two drift apart.

// The ceiling on one streamed turn: the server aborts the LLM call past it.
export const GPT_STREAM_TIMEOUT_MS = 10 * 60 * 1000;

// An `activeTurnStartedAt` older than this is a DEAD marker, not a turn in
// flight: past the stream ceiling the instance died mid-turn and no answer is
// coming. DERIVED from the ceiling rather than restated, so raising the
// ceiling cannot leave a reader declaring live turns dead. The 30s buffer
// absorbs clock skew between instances, and between an instance and a phone.
export const GPT_ACTIVE_TURN_STALE_MS = GPT_STREAM_TIMEOUT_MS + 30_000;

/**
 * The turn a conversation row says is being written right now, or null when
 * nothing is — no marker, or a marker too old to describe anything still
 * running.
 *
 * `startedAt` is a `Date` on the server and its ISO string on the wire; both
 * are accepted so the one reading of "live" serves the route and the client.
 * `nowMs` is a parameter, never `Date.now()` read inside: the caller decides
 * the clock, and a test can hold it still.
 */
export function liveActiveTurn(
  row: {
    activeTurnStartedAt: Date | string | null;
    activeTurnQuestion: string | null;
  },
  nowMs: number,
): { question: string; startedAt: string } | null {
  if (row.activeTurnStartedAt === null || row.activeTurnQuestion === null)
    return null;
  const startedAt =
    typeof row.activeTurnStartedAt === "string"
      ? new Date(row.activeTurnStartedAt)
      : row.activeTurnStartedAt;
  const startedAtMs = startedAt.getTime();
  if (Number.isNaN(startedAtMs)) return null;
  if (nowMs - startedAtMs > GPT_ACTIVE_TURN_STALE_MS) return null;
  return {
    question: row.activeTurnQuestion,
    startedAt: startedAt.toISOString(),
  };
}
